import React from 'react';
import { Button, Tooltip } from '@mui/material';
import { toast } from 'react-toastify';
import { useGlass } from 'styles/glass';

/**
 * A Button that can explain why it is disabled.
 *
 * The normal `disabled` attribute is a dead end for the user: the control
 * greys out and the browser stops sending it events, so there is nowhere to
 * hang an explanation. On a phone that is the whole story — no hover, no
 * cursor, no way to find out that Submit is waiting on a field two tabs away.
 *
 * So when a `disabledReason` is given we *soft-disable* instead:
 *
 *   - `aria-disabled` marks it disabled for assistive tech, but the element
 *     stays focusable and keeps receiving pointer events
 *   - hover / focus / long-press shows the reason in a tooltip above it
 *   - an actual click is swallowed (the real `onClick` never runs) and the
 *     reason is toasted instead, which is the only channel that works on
 *     touch where there is no hover state at all
 *
 * Pass `disabled` with no reason and you get an ordinary disabled button.
 *
 *   <GlassButton
 *     disabled={!isValid}
 *     disabledReason='Fill in Brand name and Brand domain on the Information tab.'
 *     onClick={save}
 *   >Submit</GlassButton>
 */
export default function GlassButton({
  disabled = false,
  disabledReason = '',
  onClick,
  sx,
  children,
  ...rest
}) {
  const GLASS = useGlass();
  const softDisabled = disabled && Boolean(disabledReason);

  const handleClick = (event) => {
    if (softDisabled) {
      // Stop the click dead: no form submit, no parent handler, no navigation.
      event.preventDefault();
      event.stopPropagation();
      // Keyed on the reason itself, so hammering the button re-shows the same
      // toast instead of stacking a column of identical ones — but a
      // *different* reason still gets through.
      toast.warning(disabledReason, { toastId: `disabled:${disabledReason}` });
      return;
    }
    if (onClick) onClick(event);
  };

  const button = (
    <Button
      {...rest}
      // Only really disable it when there is nothing to explain. Otherwise the
      // element has to stay alive to receive the hover and the click.
      disabled={disabled && !softDisabled}
      aria-disabled={disabled || undefined}
      onClick={handleClick}
      sx={{
        transition: 'background-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
        ...(softDisabled
          ? {
              // Mirror the look of a real disabled button — the light goes out
              // — without taking the events with it. `cursor: not-allowed`
              // signals it is refusing before the user commits to the click.
              cursor: 'not-allowed',
              color: GLASS.buttonDisabledText,
              backgroundColor: GLASS.buttonDisabled,
              boxShadow: GLASS.buttonDisabledRim,
              // The hover still lights up slightly. That is deliberate: it is
              // the affordance that says "this will tell you something".
              '&:hover': {
                backgroundColor: GLASS.buttonQuietHover,
                boxShadow: GLASS.buttonDisabledRim,
              },
            }
          : {
              '&:hover': { transform: 'translateY(-1px)' },
              '&:active': { transform: 'translateY(0)' },
            }),
        ...sx,
      }}
    >
      {children}
    </Button>
  );

  if (!softDisabled) return button;

  return (
    <Tooltip
      title={disabledReason}
      placement='top'
      arrow
      // Touch devices have no hover: 0 delay makes a long-press surface the
      // tooltip immediately, and the leave delay keeps it up long enough to
      // actually read after the finger lifts.
      enterTouchDelay={0}
      leaveTouchDelay={4000}
    >
      {/* Tooltip needs a live child to bind to — which is exactly why this
          component soft-disables rather than using `disabled`. */}
      {button}
    </Tooltip>
  );
}
